import { type Constraint, entitiesOf } from '@/domain/constraints';
import { type EntityId } from '@/domain/model';

/**
 * Constraint clustering.
 *
 * Constraints that share no variables are independent problems. Solving them together
 * would build one large system where a 5 000-entity scene with one constrained triangle
 * produces a 10 000-variable least-squares problem instead of a six-variable one —
 * and the cost of the normal equations is cubic in the variable count.
 *
 * Union-find over the constraint graph partitions them. Near-linear, and it runs once
 * per structural change rather than per frame.
 */

export interface Cluster {
  readonly constraints: readonly Constraint[];
  readonly entities: ReadonlySet<EntityId>;
}

class DisjointSet {
  private readonly parent = new Map<EntityId, EntityId>();
  private readonly rank = new Map<EntityId, number>();

  find(id: EntityId): EntityId {
    const current = this.parent.get(id);
    if (current === undefined) {
      this.parent.set(id, id);
      this.rank.set(id, 0);
      return id;
    }
    if (current === id) return id;

    // Path compression: flatten as we go, so repeated lookups stay near-constant.
    const root = this.find(current);
    this.parent.set(id, root);
    return root;
  }

  union(a: EntityId, b: EntityId): void {
    const rootA = this.find(a);
    const rootB = this.find(b);
    if (rootA === rootB) return;

    const rankA = this.rank.get(rootA) ?? 0;
    const rankB = this.rank.get(rootB) ?? 0;

    if (rankA < rankB) {
      this.parent.set(rootA, rootB);
    } else if (rankA > rankB) {
      this.parent.set(rootB, rootA);
    } else {
      this.parent.set(rootB, rootA);
      this.rank.set(rootA, rankA + 1);
    }
  }
}

/**
 * Partitions constraints into independent clusters.
 *
 * Order is deterministic — clusters come out in the order their first constraint appears,
 * and constraints keep their relative order within a cluster. The solver walks them in
 * that order, so a deterministic partition is what makes a solved sketch reproducible
 * between runs and between machines.
 */
export function findClusters(constraints: readonly Constraint[]): readonly Cluster[] {
  if (constraints.length === 0) return [];

  const sets = new DisjointSet();
  for (const constraint of constraints) {
    const entities = entitiesOf(constraint);
    const first = entities[0];
    if (first === undefined) continue;
    for (const entity of entities) sets.union(first, entity);
  }

  const byRoot = new Map<EntityId, { constraints: Constraint[]; entities: Set<EntityId> }>();
  const order: EntityId[] = [];

  for (const constraint of constraints) {
    const entities = entitiesOf(constraint);
    const first = entities[0];
    if (first === undefined) continue;

    const root = sets.find(first);
    let bucket = byRoot.get(root);
    if (bucket === undefined) {
      bucket = { constraints: [], entities: new Set<EntityId>() };
      byRoot.set(root, bucket);
      order.push(root);
    }
    bucket.constraints.push(constraint);
    for (const entity of entities) bucket.entities.add(entity);
  }

  return order.map((root) => {
    const bucket = byRoot.get(root);
    return {
      constraints: bucket?.constraints ?? [],
      entities: bucket?.entities ?? new Set<EntityId>(),
    };
  });
}

/** The clusters touched by a set of changed entities — the ones a drag must re-solve. */
export function clustersAffecting(
  clusters: readonly Cluster[],
  changed: ReadonlySet<EntityId>
): readonly Cluster[] {
  if (changed.size === 0) return [];
  return clusters.filter((cluster) => {
    for (const entity of cluster.entities) {
      if (changed.has(entity)) return true;
    }
    return false;
  });
}
